import React, { Fragment, useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import {
  faCircleX,
  faArrowsRotate,
  faXmarkToSlot,
} from "@fortawesome/pro-solid-svg-icons";
import { capitalize } from "../../../util/stringFormat";
import { useAcknowledgeFailure, useRejectFailure } from "../../creatorServices";
import {
  useCurrentOrgId,
  useIsAdmin,
  useIsCreatorManager,
  useIsUserIAPManager,
} from "../../../auth/authUtils";
import { useMediaTable } from "../../common/use-mediaTable";
import { allTrue, anyTrue, notBlank } from "../../../util/ext";
import { getType } from "../../../util/mediaUtil";
import { any, equals, filter, includes, isEmpty, propEq, sort } from "ramda";
import { useSelected } from "../OrganizeProvider";
import { useReprocessSubmission } from "../upload/uploadServices";

import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

import ContentOriginCard from "../../common/ContentOriginCard";
import CustomTablePagination from "../../common/CustomTablePagination";
import { EmptyTableText } from "./OrganizeReviewTable";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import SortLabel from "../../common/SortLabel";
import ConfirmButton from "../../../components/ConfirmButton";

const REPROCESS_THRESHOLD = 3; // days

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: "20px",
    position: "relative",
  },

  container: {
    height: "calc(100vh - 390px)",
    padding: 0,
  },

  containerWithHeader: {
    height: "calc(100vh - 480px)",
  },

  empty: {
    fontWeight: 600,
  },

  failed: {
    fontWeight: 600,
    color: theme.palette.failed,
  },

  inbox: {
    color: theme.palette.primary.darker,
  },

  largeCell: {
    width: "37.5%",
  },

  medCell: {
    width: "9%",
  },

  medReviewCell: {
    width: "12%",
  },

  origin: {
    paddingLeft: 15,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },

  smallCell: {
    width: "5%",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const formatStatus = status => {
  switch (status) {
    case "FAILURE":
      return "Failed";
    default:
      return capitalize(status);
  }
};

const getDaysElapsedSinceUpload = content => {
  const currentDate = new Date();
  const uploadDate = new Date(content.uploadDate);
  const timeDifference = currentDate.getTime() - uploadDate.getTime();
  const oneDayInMilliseconds = 1000 * 60 * 60 * 24;
  const daysElapsed = Math.floor(timeDifference / oneDayInMilliseconds);
  return daysElapsed;
};

const getSelectableContent = contentList =>
  filter(content => {
    const daysElapsed = getDaysElapsedSinceUpload(content);
    return daysElapsed >= REPROCESS_THRESHOLD;
  }, contentList);

const OrganizeProcessingTable = props => {
  const {
    contentList,
    currentStatus = "processing",
    loading,
    onPageChange,
    onRowsPerPageChange,
    onScrollVisible,
    onSortChange,
    order,
    orderBy,
    page,
    rowsPerPage,
    total,
    itemsPerPage = 10,
  } = props;
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();
  const isIAPManager = useIsUserIAPManager();
  const currentOrgId = useCurrentOrgId();

  const userCanUpdate = isAdmin || isCreatorManager || isIAPManager;

  const acknowledgeFailure = useAcknowledgeFailure();
  const rejectFailure = useRejectFailure();
  const reprocess = useReprocessSubmission();

  // wait 3 days before reprocessing or clearing
  const canReprocess = content => {
    const daysElapsed = getDaysElapsedSinceUpload(content);
    return allTrue([
      isAdmin,
      daysElapsed >= REPROCESS_THRESHOLD,
      includes(content.status, ["PENDING", "PROCESSING"]),
    ]);
  };

  const canClear = content => {
    const daysElapsed = getDaysElapsedSinceUpload(content);
    return allTrue([
      !isAdmin,
      daysElapsed >= REPROCESS_THRESHOLD,
      includes(content.status, ["FAILURE", "PENDING", "PROCESSING"]),
    ]);
  };

  const selectable = content => {
    const daysElapsed = getDaysElapsedSinceUpload(content);
    return anyTrue([
      content.status === "FAILURE",
      allTrue([
        daysElapsed >= REPROCESS_THRESHOLD,
        includes(content.status, ["PENDING", "PROCESSING"]),
      ]),
    ]);
  };

  const {
    appendToSelectedContent,
    removeAllSelectedContent,
    selectedContent,
    updateActiveSelectedMediaType,
    updateSelectedContent,
  } = useSelected();

  const {
    handleRequestSort,
    handleChangePage,
    handleUpdateRowsPerPage,
  } = useMediaTable(
    { onPageChange, onRowsPerPageChange, onSortChange },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      rowsPerPage,
    },
  );

  const [orderedList, setOrderedList] = useState(contentList);
  useEffect(() => {
    //TODO: default to dateAdded desc for now
    const list = sort((a, b) => {
      const c = new Date(a.uploadDate);
      const d = new Date(b.uploadDate);
      return d - c;
    }, contentList);

    setOrderedList(list);
  }, [contentList]);

  //-- Track if scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(contentList)) {
      const element = tableContainerEl.current;
      if (element) {
        const scrollVisible = element.scrollHeight > element.clientHeight;
        onScrollVisible(scrollVisible);
      }
    }
  }, [onScrollVisible, contentList]);

  //-- Manage Checkbox State
  const [checkbox, setCheckbox] = useState({
    checked: false,
    indeterminate: false,
  });
  useEffect(() => {
    if (!isEmpty(selectedContent)) {
      const selectableContent = getSelectableContent(contentList);
      setCheckbox({
        checked: equals(selectedContent.length, selectableContent.length),
        indeterminate: !equals(
          selectedContent.length,
          selectableContent.length,
        ),
      });
    } else {
      setCheckbox({ checked: false, indeterminate: false });
    }
  }, [contentList, selectedContent]);

  const handleChange = event => {
    appendToSelectedContent(checkbox, getSelectableContent(contentList));
  };

  const handleRowClick = content => event => {
    if (selectable(content)) {
      updateSelectedContent(content);
      updateActiveSelectedMediaType("content");
    }
  };

  /** Processing Handlers */
  const handleReprocess = content => () =>
    reprocess.mutateAsync({
      resourceIds: {
        submissionId: content.mediaId,
      },
    });

  const handleAcknowledgeFailure = content => () =>
    acknowledgeFailure.mutateAsync({
      resourceIds: {
        orgId: currentOrgId,
        submissionId: content.mediaId,
      },
    });

  const handleRejectFailure = content => () =>
    rejectFailure.mutateAsync({
      resourceIds: {
        orgId: currentOrgId,
        submissionId: content.mediaId,
      },
    });
  /** End Processing Handlers */

  return (
    <Paper elevation={0} className={classes.root}>
      {anyTrue([
        loading,
        acknowledgeFailure.isLoading,
        rejectFailure.isLoading,
        reprocess.isLoading,
      ]) && <PanelLoadingIndicator transparent />}

      {total === 0 ? (
        <EmptyTableText status={currentStatus} />
      ) : (
        <Fragment>
          <TableContainer
            ref={tableContainerEl}
            className={clsx(classes.container)}
          >
            <Table stickyHeader aria-label="Content">
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    align="center"
                    className={classes.smallCell}
                  >
                    <Checkbox
                      disabled={!userCanUpdate}
                      color="primary"
                      indeterminate={checkbox.indeterminate}
                      checked={checkbox.checked}
                      onChange={handleChange}
                      inputProps={{ "aria-label": "Select" }}
                    />
                  </TableCell>

                  <TableCell
                    key="content"
                    align="left"
                    className={classes.largeCell}
                  >
                    <SortLabel
                      state={{
                        orderBy,
                        orderDirection: order,
                      }}
                      property="title"
                      label="Title"
                      handleRequestSort={handleRequestSort}
                    />
                  </TableCell>

                  <TableCell
                    key="date-uploaded"
                    align="left"
                    className={classes.medReviewCell}
                  >
                    <SortLabel
                      state={{
                        orderBy,
                        orderDirection: order,
                      }}
                      property="uploadDate"
                      label="Uploaded"
                      handleRequestSort={handleRequestSort}
                    />
                  </TableCell>

                  <TableCell
                    key="content-type"
                    align="left"
                    className={classes.medReviewCell}
                  >
                    <SortLabel
                      state={{
                        orderBy,
                        orderDirection: order,
                      }}
                      property="contentType"
                      label="Type"
                      handleRequestSort={handleRequestSort}
                    />
                  </TableCell>

                  <TableCell
                    key="origin"
                    align="left"
                    className={clsx(classes.medReviewCell, classes.origin)}
                  >
                    <SortLabel
                      state={{
                        orderBy,
                        orderDirection: order,
                      }}
                      property="sources"
                      label="Origin"
                      handleRequestSort={handleRequestSort}
                    />
                  </TableCell>

                  <TableCell key="status">
                    <SortLabel
                      state={{
                        orderBy,
                        orderDirection: order,
                      }}
                      property="status"
                      label="Status"
                      handleRequestSort={handleRequestSort}
                    />
                  </TableCell>

                  {userCanUpdate && (
                    <TableCell
                      className={classes.smallCell}
                      style={{ paddingLeft: 12 }}
                      key="action"
                    >
                      Action
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {orderedList.map((content, index) => (
                  <TableRow
                    key={content.id}
                    style={{ height: 56 }}
                    hover
                    onClick={handleRowClick(content)}
                    tabIndex={-1}
                    role="checkbox"
                    aria-checked={any(
                      propEq("id", content.id),
                      selectedContent,
                    )}
                    selected={any(propEq("id", content.id), selectedContent)}
                  >
                    <TableCell padding="checkbox" align="center">
                      <Checkbox
                        disabled={!selectable(content)}
                        color="primary"
                        checked={any(propEq("id", content.id), selectedContent)}
                        inputProps={{
                          "aria-labelledby":
                            "content-checkbox-" + content.mediaId,
                        }}
                      />
                    </TableCell>

                    <TableCell>{content.title}</TableCell>

                    <TableCell>
                      {content.uploadDate
                        ? new Date(content.uploadDate).toLocaleDateString()
                        : ""}
                    </TableCell>

                    <TableCell style={{ textTransform: "capitalize" }}>
                      {getType(content)}
                    </TableCell>

                    <TableCell>
                      <ContentOriginCard content={content} />
                    </TableCell>

                    <TableCell
                      className={clsx({
                        [classes.failed]: content.status === "FAILURE",
                      })}
                    >
                      {formatStatus(content.status)}
                    </TableCell>

                    {userCanUpdate && (
                      <TableCell align="center">
                        <Box alignItems="center" display="flex" width={120}>
                          {canReprocess(content) && (
                            <ConfirmButton
                              variant="darker"
                              icon={faArrowsRotate}
                              handleDelete={handleReprocess(content)}
                              handleSuccess={() => removeAllSelectedContent()}
                              tooltipTitle="Reprocess"
                              dialogTitle="Reprocess Item"
                              dialogConfirmText="Reprocess"
                              dialogSuccessMessage="Item Reprocessing"
                              dialogBody="Send this item back through processing?"
                            />
                          )}

                          {canClear(content) && (
                            <ConfirmButton
                              variant="darker"
                              icon={faCircleX}
                              handleDelete={handleAcknowledgeFailure(content)}
                              handleSuccess={() => removeAllSelectedContent()}
                              tooltipTitle="Clear"
                              dialogTitle="Clear Item"
                              dialogConfirmText="Clear"
                              dialogSuccessMessage="Item Cleared"
                              dialogBody="Clear this item from processing?"
                            />
                          )}

                          {allTrue([
                            includes(content.status, [
                              "FAILURE",
                              "PENDING",
                              "PROCESSING",
                            ]),
                            isIAPManager,
                            !isAdmin,
                          ]) && (
                            <ConfirmButton
                              variant="darker"
                              icon={faXmarkToSlot}
                              handleDelete={handleRejectFailure(content)}
                              handleSuccess={() => removeAllSelectedContent()}
                              tooltipTitle="Reject"
                              dialogTitle="Reject Item"
                              dialogConfirmText="Reject"
                              dialogSuccessMessage="Item Rejected"
                              dialogBody="Reject this item and clear from processing?"
                            />
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CustomTablePagination
            classes={{
              root: classes.pagination,
            }}
            component="div"
            count={total}
            rowsPerPage={itemsPerPage}
            page={page}
            onChangePage={(event, page) => {
              handleChangePage(page);
            }}
            onChangeRowsPerPage={event =>
              handleUpdateRowsPerPage(+event.target.value)
            }
          />
        </Fragment>
      )}
    </Paper>
  );
};

export default OrganizeProcessingTable;
